import React from 'react'
import Select from 'react-select'
import { classNames } from '../_utils'
import { useField, useFormikContext } from 'formik'

export default function SearchableSelectField({
    spaced,
    disabled = false,
    required = false,
    question,
    name,
    iconComponent,
    label,
    options,
    tooltip,
    placeholder = "Choose..."
}) {
    const { setFieldValue, setFieldTouched } = useFormikContext();
    const [field, meta] = useField(name);

    // Convert simple options to react-select format if needed, though usually we expect { value, label }
    // The existing options map in CertificateManagement.js returns { id, label, value } which is perfect.

    const handleChange = (option) => {
        setFieldValue(name, option ? option.value : '');
    };

    const handleBlur = () => {
        setFieldTouched(name, true);
    };

    // Find the selected option object corresponding to the current value
    const selectedOption = options?.find(option => option.value === field.value) || null;

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '2rem', // h-8
            height: '2rem',
            borderRadius: '0.125rem', // rounded-sm
            borderColor: meta.touched && meta.error ? '#ef4444' : '#6b7280', // red-500 : gray-500
            boxShadow: 'none',
            '&:hover': {
                borderColor: meta.touched && meta.error ? '#ef4444' : '#6b7280',
            },
            fontSize: '0.875rem', // text-sm
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 8px',
            height: '2rem',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
            padding: '0px',
            color: '#374151', // text-gray-700
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '2rem',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '4px',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999, // Ensure it sits on top of other elements/modals
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '0.875rem', // text-sm
            color: '#374151', // text-gray-700
            backgroundColor: state.isSelected ? '#e5e7eb' : state.isFocused ? '#f3f4f6' : 'white',
            cursor: 'pointer',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#374151', // text-gray-700
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#000000', // placeholder-black (or close to it)
        }),
    };

    return (
        <div className={`flex flex-col items-start justify-center py-1 w-full ${spaced ? "md:my-3 lg:my-3 my-2" : "my-0"}`}>
            <p className="text-gray-700 text-xs">
                {question}{required && (<span className="text-red-500">*</span>)} <span className="text-xs text-blue-600">{tooltip}</span>
            </p>
            <div className={classNames((meta.touched && meta.error) ? "inline-flex w-full rounded-sm border border-red-500 h-8 my-1 mb-3 relative" : "inline-flex w-full rounded-sm border border-gray-500 h-8 my-1 mb-3 relative")} >
                {iconComponent && (
                    <div className="w-1/6 flex items-center justify-center bg-gray-50 border-r border-gray-300">
                        {iconComponent}
                    </div>
                )}
                <div className={iconComponent ? "w-5/6" : "w-full"}>
                    <Select
                        name={name}
                        id={name}
                        value={selectedOption}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        options={options}
                        isDisabled={disabled}
                        styles={customStyles}
                        placeholder={placeholder}
                        isClearable={true}
                        isSearchable={true}
                        classNamePrefix="react-select"
                        components={{
                            IndicatorSeparator: () => null
                        }}
                    />
                </div>

                {meta.touched && meta.error && (
                    <p className="absolute -bottom-6 left-0 text-red-500 py-1 text-sm">
                        {meta.error}
                    </p>
                )}
            </div>
        </div>
    )
}
